import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  clearToken,
  deleteMyData,
  downloadReceipt,
  emailMyReceipt,
  fetchPrivacy,
  fetchPrivacyManifest,
  fetchReceipt,
  setMaskByDefault,
} from "../lib/api";
import { ArrowLeft, EyeOff, FileDown, Globe, Lock, Trash2 } from "lucide-react";

const Section = ({ n, title, lead, children }) => (
  <section className="border-t border-[var(--line)] pt-10">
    <p className="label">{n}</p>
    <h2 className="mt-3 font-display text-2xl leading-tight tracking-tight sm:text-3xl">{title}</h2>
    {lead && <p className="mt-4 max-w-2xl text-sm leading-relaxed text-zinc-400">{lead}</p>}
    <div className="mt-7">{children}</div>
  </section>
);

export default function Privacy() {
  const [manifest, setManifest] = useState(null);
  const [mine, setMine] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [emailing, setEmailing] = useState(false);
  const [emailNote, setEmailNote] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    fetchPrivacyManifest()
      .then((d) => setManifest(d.manifest))
      .catch(() => {});
    fetchPrivacy()
      .then(setMine)
      .catch(() => {});
    fetchReceipt()
      .then(setReceipt)
      .catch(() => {});
  }, []);

  const toggleDefault = async () => {
    const next = !mine?.settings?.mask_by_default;
    setBusy(true);
    try {
      await setMaskByDefault(next);
      setMine(await fetchPrivacy());
    } finally {
      setBusy(false);
    }
  };

  const erase = async () => {
    setBusy(true);
    try {
      await deleteMyData();
      clearToken();
      nav("/", { replace: true });
    } finally {
      setBusy(false);
    }
  };

  if (!manifest) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="label">Loading the statement…</p>
      </div>
    );
  }

  const m = manifest;
  const d = mine?.your_data;

  return (
    <div
      data-testid="privacy-page"
      className="grain thin-scroll h-full overflow-y-auto bg-[var(--void)]"
    >
      <div className="mx-auto max-w-3xl px-6 py-16 sm:px-10">
        <Link
          to="/"
          data-testid="privacy-back-link"
          className="label flex items-center gap-2 transition-colors hover:text-zinc-200"
        >
          <ArrowLeft size={11} strokeWidth={1.7} /> Back
        </Link>
        <Link
          to="/legal"
          data-testid="privacy-legal-link"
          className="label ml-6 underline decoration-[var(--line-hi)] underline-offset-4 hover:text-zinc-200"
        >
          Terms, privacy policy and waiver →
        </Link>

        <p className="label mt-12">Privacy statement · v{m.version}</p>
        <h1 className="mt-5 font-display text-4xl leading-[0.95] tracking-tight sm:text-5xl">
          What we do with
          <br />
          <span className="italic text-zinc-400">what you tell us.</span>
        </h1>
        <p
          data-testid="privacy-headline"
          className="mt-8 max-w-2xl border-l border-[var(--line-hi)] pl-5 text-base leading-relaxed text-zinc-200"
        >
          {m.headline}
        </p>
        <p className="mt-6 max-w-2xl text-sm leading-relaxed text-zinc-500">
          This page is generated from the same configuration the server runs on, so it cannot
          quietly drift away from what the code actually does.
        </p>

        <div className="mt-16 space-y-14">
          <Section
            n="01"
            title="What is collected"
            lead="Only what the instrument needs to draw your map. There is no tracking layer, no advertising pixel and no session recorder anywhere in this application."
          >
            <ul data-testid="privacy-collected" className="space-y-7">
              {m.collected.map((c) => (
                <li key={c.what} className="grid gap-2 border-b border-[var(--line)] pb-6">
                  <p className="text-sm text-zinc-100">{c.what}</p>
                  <p className="text-sm leading-relaxed text-zinc-500">{c.why}</p>
                  <p className="font-data text-[10px] uppercase tracking-[0.14em] text-zinc-700">
                    {c.where}
                  </p>
                </li>
              ))}
            </ul>
          </Section>

          <Section
            n="02"
            title="What leaves this system"
            lead="The honest answer is: some of it has to. A language model conducts the interview, so it reads your answers. Everything else is contained, and this is the exhaustive list."
          >
            <ul data-testid="privacy-outbound" className="space-y-7">
              {m.leaves_the_system.map((o) => (
                <li key={o.to} className="border border-[var(--line)] px-5 py-5">
                  <div className="flex flex-wrap items-baseline gap-3">
                    <Globe size={12} strokeWidth={1.6} className="text-zinc-500" />
                    <p className="font-data text-[11px] uppercase tracking-[0.16em] text-zinc-200">
                      {o.to}
                    </p>
                    <span className="label ml-auto">
                      {o.avoidable ? "avoidable" : "required"}
                    </span>
                  </div>
                  <p className="mt-4 text-sm leading-relaxed text-zinc-300">{o.what}</p>
                  <p className="mt-3 font-data text-[10px] leading-relaxed text-zinc-600">
                    {o.retention}
                    {o.how_to_avoid ? ` · ${o.how_to_avoid}` : ""}
                  </p>
                </li>
              ))}
            </ul>
          </Section>

          <Section n="03" title="What never happens">
            <ul data-testid="privacy-never" className="space-y-3">
              {m.never.map((n) => (
                <li key={n} className="flex gap-4 text-sm leading-relaxed text-zinc-300">
                  <Lock size={12} strokeWidth={1.6} className="mt-1 shrink-0 text-zinc-600" />
                  {n}
                </li>
              ))}
            </ul>
          </Section>

          <Section n="04" title="Who can read it">
            <ul data-testid="privacy-readers" className="space-y-3">
              {m.who_can_read_it.map((w) => (
                <li key={w} className="text-sm leading-relaxed text-zinc-300">
                  — {w}
                </li>
              ))}
            </ul>
          </Section>

          <Section
            n="05"
            title="Masking, precisely"
            lead="You can withhold any single answer from the operator. It is worth being exact about what that does and does not buy you."
          >
            <div data-testid="privacy-masking" className="space-y-5">
              {[
                ["What it does", m.masking.what_it_does],
                ["What it does not do", m.masking.what_it_does_not_do],
                ["Effect on your scores", m.masking.measurement],
                ["Mask everything", m.masking.default_mode],
              ].map(([k, v]) => (
                <div key={k} className="border-l border-[var(--line-hi)] pl-5">
                  <p className="label">{k}</p>
                  <p className="mt-2 text-sm leading-relaxed text-zinc-300">{v}</p>
                </div>
              ))}
            </div>
          </Section>

          <Section n="06" title="Your controls">
            <ul data-testid="privacy-controls" className="space-y-3">
              {m.your_controls.map((c) => (
                <li key={c} className="text-sm leading-relaxed text-zinc-300">
                  — {c}
                </li>
              ))}
            </ul>
            <p className="mt-7 font-data text-[10px] leading-relaxed text-zinc-600">
              RETENTION · {m.retention}
            </p>
          </Section>

          {mine && (
            <Section
              n="07"
              title="Your engagement, right now"
              lead="Not boilerplate — these are live figures from your own session."
            >
              <div className="grid grid-cols-2 gap-px bg-[var(--line)] sm:grid-cols-4">
                {[
                  ["Turns on record", d.turns_on_record],
                  ["Masked by you", d.masked_turns],
                  ["Categories filled", `${d.categories_filled}/${d.categories_total ?? 46}`],
                  ["Facets named", `${d.facets_named ?? 0}`],
                  ["Public searches", d.searches_run_on_your_behalf.length],
                ].map(([k, v]) => (
                  <div key={k} className="border border-[var(--line)] px-4 py-4">
                    <p className="label">{k}</p>
                    <p className="mt-2 font-data text-xl tabular-nums leading-none text-zinc-100">
                      {v}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-4 border border-[var(--line)] px-5 py-5">
                <EyeOff size={13} strokeWidth={1.7} className="text-zinc-400" />
                <div className="min-w-[14rem] flex-1">
                  <p className="text-sm text-zinc-100">Mask every answer from the operator</p>
                  <p className="mt-1.5 font-data text-[10px] leading-relaxed text-zinc-600">
                    Nothing you say gets captured as a quote in the first place. Your scores are
                    unaffected.
                  </p>
                </div>
                <button
                  data-testid="privacy-mask-default-toggle"
                  disabled={busy}
                  onClick={toggleDefault}
                  className={`px-4 py-2 font-data text-[10px] uppercase tracking-[0.16em] transition-colors ${
                    mine.settings.mask_by_default
                      ? "bg-zinc-100 text-[var(--void)]"
                      : "border border-[var(--line-hi)] text-zinc-400 hover:text-zinc-100"
                  }`}
                >
                  {mine.settings.mask_by_default ? "On" : "Off"}
                </button>
              </div>

              <div className="mt-8">
                <p className="label">Cohort band we are willing to transmit</p>
                <p
                  data-testid="privacy-cohort-band"
                  className="mt-3 font-data text-[11px] leading-relaxed text-zinc-400"
                >
                  {Object.keys(d.cohort_band_transmittable || {}).length
                    ? Object.entries(d.cohort_band_transmittable)
                        .map(([k, v]) => `${k}: ${String(v)}`)
                        .join("  ·  ")
                    : "Nothing yet — the interview has not established a cohort frame."}
                </p>
              </div>

              {d.searches_run_on_your_behalf.length > 0 && (
                <div className="mt-8">
                  <p className="label">Exactly what was sent to a public search</p>
                  <ul data-testid="privacy-search-log" className="mt-3 space-y-4">
                    {d.searches_run_on_your_behalf.map((s) => (
                      <li key={`${s.at}-${s.query}`} className="border border-[var(--line)] p-4">
                        <p className="font-data text-[10px] uppercase tracking-[0.14em] text-zinc-500">
                          {s.category} · {s.checked_at?.slice(0, 16).replace("T", " ")}
                        </p>
                        <pre className="thin-scroll mt-2 overflow-auto font-data text-[10px] leading-relaxed text-zinc-400">
                          {JSON.stringify(s.sent, null, 2)}
                        </pre>
                        {s.queries?.length > 0 && (
                          <p className="mt-2 font-data text-[10px] leading-relaxed text-zinc-600">
                            queries: {s.queries.join(" | ")}
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-10 border border-[var(--line)] px-5 py-5">
                <div className="flex flex-wrap items-center gap-4">
                  <FileDown size={13} strokeWidth={1.7} className="text-zinc-400" />
                  <div className="min-w-[14rem] flex-1">
                    <p className="text-sm text-zinc-100">Signed consent receipt</p>
                    <p className="mt-1.5 font-data text-[10px] leading-relaxed text-zinc-600">
                      {receipt
                        ? `Receipt ${receipt.receipt.receipt_id} · accepted ${receipt.receipt.accepted_at?.slice(0, 16).replace("T", " ")} against statement v${receipt.receipt.manifest_version}. Signed with HMAC-SHA256, so any later alteration is detectable.`
                        : "A record of what you accepted and everything that was transmitted."}
                    </p>
                  </div>
                  <button
                    data-testid="privacy-receipt-btn"
                    disabled={!receipt}
                    onClick={downloadReceipt}
                    className="btn-stark"
                  >
                    Download receipt
                  </button>
                  <button
                    data-testid="privacy-receipt-email-btn"
                    disabled={!receipt || emailing}
                    onClick={async () => {
                      setEmailing(true);
                      setEmailNote("");
                      try {
                        const r = await emailMyReceipt();
                        setEmailNote(`sent to ${r.sent_to}`);
                      } catch (e) {
                        setEmailNote(
                          e?.response?.data?.detail || "could not send it just now"
                        );
                      } finally {
                        setEmailing(false);
                      }
                    }}
                    className="btn-stark"
                  >
                    {emailing ? "Sending" : "Email it to me"}
                  </button>
                </div>
                {emailNote && (
                  <p
                    data-testid="privacy-receipt-email-note"
                    className="mt-3 font-data text-[11px] text-zinc-400"
                  >
                    — {emailNote}
                  </p>
                )}
                {receipt && (
                  <p
                    data-testid="privacy-receipt-signature"
                    className="mt-4 break-all font-data text-[10px] leading-relaxed text-zinc-700"
                  >
                    {receipt.signature}
                  </p>
                )}
              </div>

              <div className="mt-10 border border-zinc-700 px-5 py-5">
                <div className="flex flex-wrap items-center gap-4">
                  <Trash2 size={13} strokeWidth={1.7} className="text-zinc-300" />
                  <div className="min-w-[14rem] flex-1">
                    <p className="text-sm text-zinc-100">Delete everything, permanently</p>
                    <p className="mt-1.5 font-data text-[10px] leading-relaxed text-zinc-600">
                      Transcript, map, telemetry and account. There is no soft delete and no backup
                      to restore from.
                    </p>
                  </div>
                  {confirming ? (
                    <div className="flex gap-2">
                      <button
                        data-testid="privacy-delete-confirm-btn"
                        disabled={busy}
                        onClick={erase}
                        className="btn-stark !bg-zinc-100 !text-[var(--void)]"
                      >
                        {busy ? "Erasing…" : "Yes, erase it"}
                      </button>
                      <button
                        data-testid="privacy-delete-cancel-btn"
                        onClick={() => setConfirming(false)}
                        className="label hover:text-zinc-200"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      data-testid="privacy-delete-btn"
                      onClick={() => setConfirming(true)}
                      className="btn-stark"
                    >
                      Delete my data
                    </button>
                  )}
                </div>
              </div>
            </Section>
          )}
        </div>

        <p className="mt-20 font-data text-[10px] leading-relaxed text-zinc-700">
          Questions about any of this should go to whoever handed you the link to this instrument.
        </p>
      </div>
    </div>
  );
}
